import React, { useCallback, useEffect, useState } from "react";
import { fetchLlmUsageSummary } from "../../services/api";

/**
 * Tenant LLM token usage and estimated cost (Settings / Admin).
 */
const LlmUsagePanel = ({ hideHeader = false, admin = false }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchLlmUsageSummary({ admin });
      setSummary(data);
    } catch (e) {
      setError(e.message || "Failed to load LLM usage");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [admin]);

  useEffect(() => {
    load();
  }, [load]);

  const fmt = (n) =>
    typeof n === "number" ? n.toLocaleString() : String(n ?? 0);
  const usd = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    })}`;

  return (
    <div className="p-4 space-y-3">
      {!hideHeader ? (
        <h3 className="text-lg font-semibold text-slate-900">LLM usage</h3>
      ) : null}
      <p className="text-sm text-slate-600">
        Tokens and estimated spend from Ask, enrichment, embeddings, and other
        gateway calls. Cost uses Vertex list prices — not an invoice. Each
        tenant has a monthly dollar budget (default $5) that gates new LLM
        calls when enforce is on.
      </p>
      <button
        type="button"
        onClick={load}
        disabled={loading}
        className="px-3 py-1.5 text-sm bg-slate-800 text-white rounded"
      >
        {loading ? "Loading…" : "Refresh"}
      </button>
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {summary ? (
        <div className="space-y-3 text-sm text-slate-800">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="text-xs text-slate-500">Total tokens</div>
              <div className="font-semibold">{fmt(summary.total_tokens)}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Input / output</div>
              <div className="font-semibold">
                {fmt(summary.input_tokens)} / {fmt(summary.output_tokens)}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Est. cost (USD)</div>
              <div className="font-semibold">
                {usd(summary.estimated_cost_usd)}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Events</div>
              <div className="font-semibold">{fmt(summary.event_count)}</div>
            </div>
          </div>
          {summary.quota ? (
            <p className="text-xs text-slate-500">
              Monthly budget:{" "}
              {usd(summary.quota.llm_monthly_cost_quota_usd ?? 5)}
              {" · "}
              used: {usd(summary.quota.cost_used_this_month_usd)}
              {" · "}
              remaining: {usd(summary.quota.cost_remaining_usd)}
              {" · "}
              tokens: {fmt(summary.quota.tokens_used_this_month)}
              {" · "}
              enforce: {summary.quota.llm_quota_enforce ? "on" : "off"}
            </p>
          ) : null}
          {Array.isArray(summary.by_model) && summary.by_model.length > 0 ? (
            <div>
              <h4 className="font-medium text-slate-900 mb-1">By model</h4>
              <ul className="space-y-1 text-xs">
                {summary.by_model.map((row) => (
                  <li key={row.model} className="flex flex-wrap gap-x-3">
                    <span className="font-medium">{row.model}</span>
                    <span>{fmt(row.total_tokens)} tokens</span>
                    <span>{usd(row.estimated_cost_usd)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default LlmUsagePanel;
